import { Button } from '@timelyapp/react';
import classNames from 'classnames';
import Avatar from 'components/Avatar';
import React from 'react';
import { IMAGE_SUPPORTED_MIME_TYPES } from 'utils/file';
import type { AvatarUploadHandlers } from '~hooks/useAvatarUploadField';
import { useAvatarUploadField } from '~hooks/useAvatarUploadField';
import styles from './AvatarUploadField.module.scss';

const ACCEPTED_FILE_TYPES = IMAGE_SUPPORTED_MIME_TYPES.join(',');

interface Props extends AvatarUploadHandlers {
  avatarUrl?: string | null;
  /** Used for the initials fallback when there is no image. */
  name?: string;
  /** Avatar width/height in px. */
  size?: number;
  /** `stacked` puts the buttons under the avatar, `inline` beside it. */
  layout?: 'stacked' | 'inline';
}

/**
 * Avatar preview with "Upload photo" / "Remove" buttons. Validates the picked
 * file (PNG/JPEG, under 2 MB) before handing it to `onUpload`.
 */
const AvatarUploadField: React.FC<Props> = ({
  avatarUrl,
  name,
  size = 120,
  layout = 'stacked',
  onUpload,
  onRemove,
}) => {
  const {
    fileInputRef,
    isUploading,
    isRemoving,
    validationError,
    openFilePicker,
    handleFileChange,
    handleRemove,
  } = useAvatarUploadField({ onUpload, onRemove });

  return (
    <div className={classNames(styles.container, styles[layout])}>
      <Avatar
        src={avatarUrl}
        name={name}
        shape="circle"
        width={size}
        height={size}
        tooltip={false}
        className={styles.avatar}
      />
      <div className={styles.controls}>
        <div className={styles.actions}>
          <Button
            data-testid="avatar_upload_button"
            priority="low"
            size="small"
            isLoading={isUploading}
            disabled={isRemoving}
            onClick={openFilePicker}
          >
            Upload photo
          </Button>
          {avatarUrl && (
            <Button
              data-testid="avatar_remove_button"
              priority="low"
              size="small"
              isLoading={isRemoving}
              disabled={isUploading}
              onClick={handleRemove}
            >
              Remove
            </Button>
          )}
          <input
            ref={fileInputRef}
            data-testid="avatar_file_input"
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            className={styles.hiddenFileInput}
            onChange={handleFileChange}
          />
        </div>
        {validationError && (
          <p role="alert" className={styles.error}>
            {validationError}
          </p>
        )}
      </div>
    </div>
  );
};

export default AvatarUploadField;
